// Rich text layout for canvas rendering: greedy word wrap with mixed fonts,
// centre/left alignment, bullets, and character breaking for over-long words.
// Positions are in world units and independent of zoom, so the text cache can
// rasterise the same layout at any scale without reflow.
import type { RichText, RichTextRun } from '../../shared/types.ts';

export type Measure = (text: string, font: string) => number;
export type Align = 'left' | 'center';

export const FONT_FAMILY = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
export const LINE_HEIGHT = 1.3;     // × font size
export const BULLET_INDENT = 1.2;   // em, hanging indent for left-aligned lists
const BULLET = '•';

export interface Frag {
  text: string;
  x: number;       // relative to the layout's left edge
  w: number;
  trail: number;   // width of trailing whitespace (not visible)
  b?: 1; i?: 1; href?: string;
  bullet?: true;   // inline bullet marker (centre-aligned lists)
}

export interface Line {
  top: number;     // relative to the layout's top edge
  y: number;       // vertical middle of the line (textBaseline 'middle')
  x: number;       // left of the first visible glyph
  w: number;       // visible width
  indent: number;
  bullet: boolean; // draw a hanging bullet (left-aligned lists)
  frags: Frag[];
}

export interface TextLayout {
  lines: Line[];
  width: number;      // widest line's right edge
  height: number;
  fontSize: number;
  lineHeight: number;
  brokeWords: boolean; // some word had to be split across lines
}

export interface LayoutOptions {
  width: number;
  fontSize: number;
  align: Align;
  family?: string;
}

export function fontString(size: number, b?: 1, i?: 1, family = FONT_FAMILY): string {
  return `${i ? 'italic ' : ''}${b ? 'bold ' : ''}${size}px ${family}`;
}

const isSpace = (s: string) => !/\S/.test(s);

/** Largest n such that the first n code points fit in `avail`. */
function fitChars(chars: string[], avail: number, font: string, measure: Measure): number {
  let lo = 0, hi = chars.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (measure(chars.slice(0, mid).join(''), font) <= avail) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

export function layoutText(rt: RichText, opts: LayoutOptions, measure: Measure): TextLayout {
  const { width, fontSize: size, align } = opts;
  const family = opts.family ?? FONT_FAMILY;
  const lh = size * LINE_HEIGHT;
  const lines: Line[] = [];
  let brokeWords = false;
  const blocks = rt.blocks.length ? rt.blocks : [{ runs: [] }];

  for (const block of blocks) {
    const hanging = !!block.list && align === 'left';
    const indent = hanging ? size * BULLET_INDENT : 0;
    const avail = Math.max(size, width - indent);
    let frags: Frag[] = [];
    let lineW = 0;
    let first = true;

    const push = () => {
      // Re-measure merged frags so positions match how fillText will draw them.
      let x = 0;
      for (const f of frags) {
        f.w = measure(f.text, fontString(size, f.b, f.i, family));
        f.x = x;
        x += f.w;
      }
      const last = frags[frags.length - 1];
      lines.push({ top: 0, y: 0, x: 0, w: x - (last?.trail ?? 0), indent, bullet: hanging && first, frags });
      frags = [];
      lineW = 0;
      first = false;
    };

    const add = (text: string, w: number, r: Partial<RichTextRun>, bullet = false) => {
      const prev = frags[frags.length - 1];
      const space = isSpace(text);
      if (prev && !prev.bullet && !bullet && prev.b === r.b && prev.i === r.i && prev.href === r.href) {
        prev.text += text;
        prev.trail = space ? prev.trail + w : 0;
      } else {
        const f: Frag = { text, x: 0, w, trail: space ? w : 0 };
        if (r.b) f.b = 1;
        if (r.i) f.i = 1;
        if (r.href) f.href = r.href;
        if (bullet) f.bullet = true;
        frags.push(f);
      }
      lineW += w;
    };

    const hasContent = () => frags.some((f) => !f.bullet);

    if (block.list && !hanging) {
      const marker = `${BULLET} `;
      add(marker, measure(marker, fontString(size, undefined, undefined, family)), {}, true);
    }

    for (const r of block.runs) {
      const font = fontString(size, r.b, r.i, family);
      for (const tok of r.t.match(/\s+|\S+/g) ?? []) {
        const w = measure(tok, font);
        if (isSpace(tok)) {
          // Whitespace hangs at line ends; it's dropped at the start of wrapped lines.
          if (first || hasContent()) add(tok, w, r);
          continue;
        }
        if (lineW + w > avail && hasContent()) push();
        if (lineW + w <= avail) {
          add(tok, w, r);
          continue;
        }
        // Word wider than a whole line: break it by characters.
        brokeWords = true;
        let chars = Array.from(tok);
        while (chars.length) {
          let n = fitChars(chars, avail - lineW, font, measure);
          if (n === 0) {
            if (hasContent()) { push(); continue; }
            n = 1;
          }
          const part = chars.slice(0, n).join('');
          add(part, measure(part, font), r);
          chars = chars.slice(n);
          if (chars.length) push();
        }
      }
    }
    push(); // every block ends its line; an empty block is one blank line
  }

  let maxW = 0;
  lines.forEach((ln, i) => {
    ln.top = i * lh;
    ln.y = ln.top + lh / 2;
    ln.x = ln.indent + (align === 'center' ? Math.max(0, (width - ln.indent - ln.w) / 2) : 0);
    for (const f of ln.frags) f.x += ln.x;
    maxW = Math.max(maxW, ln.x + ln.w);
  });
  return { lines, width: maxW, height: lines.length * lh, fontSize: size, lineHeight: lh, brokeWords };
}

/**
 * Lays out at the largest size ≤ maxSize whose text fits `height` without
 * splitting words (sticky-note behaviour), never going below minSize.
 */
export function fitText(
  rt: RichText, width: number, height: number, align: Align,
  maxSize: number, minSize: number, measure: Measure,
): TextLayout {
  let size = maxSize;
  for (;;) {
    const layout = layoutText(rt, { width, fontSize: size, align }, measure);
    if ((layout.height <= height && !layout.brokeWords) || size <= minSize) return layout;
    size = Math.max(minSize, Math.round(size * 0.9 * 100) / 100);
  }
}

/** Hanging bullet position relative to the layout's left edge. */
export function bulletX(line: Line, fontSize: number): number {
  return line.indent - fontSize * 0.85;
}

export { BULLET };
